import { PropsWithChildren } from 'react'

import { GeneralField, isVoidField } from '@formily/core'
import { observer, useField } from '@formily/react'
import { createStyles } from 'antd-style'
import { isString } from 'lodash'

import { isEmpty } from '@eastcoal/common/utils/object'

import { BaseFormItem } from './BaseItem'
import {
  BaseFormItemProps,
  FormItemErrorListProps,
  FormItemProps
} from './interface'

const useErrorListStyles = createStyles(({ token }) => ({
  list: {
    margin: 0,
    padding: 0,
    listStyleType: 'decimal',
    listStylePosition: 'inside',
    display: 'flex',
    alignItems: 'center',
    gap: token.paddingXS,
    [`&.is-vertical`]: {
      flexDirection: 'column'
    }
  },
  item: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden'
  }
}))

const ErrorList = ({ vertical, errors }: FormItemErrorListProps) => {
  const uniqueErrors = Array.from(new Set(errors))
  const { cx, styles } = useErrorListStyles()
  if (isEmpty(errors)) {
    return null
  }
  if (uniqueErrors.length === 1) {
    return uniqueErrors[0]
  }
  return (
    <ul
      className={cx(styles.list, {
        ['is-vertical']: vertical
      })}
    >
      {uniqueErrors.map((error, index) => (
        <li
          key={error}
          className={styles.item}
          data-index={index + 1}
          title={error}
        >
          {error}
        </li>
      ))}
    </ul>
  )
}

const useFormItemProps = (props: FormItemProps): BaseFormItemProps => {
  const field = useField<GeneralField>()
  if (!field || isVoidField(field)) {
    return props
  }
  const { showLabel = true, asterisk, feedbackLayout } = props
  const errors = field.selfErrors.filter(isString)
  const hasErrors = !isEmpty(errors)
  return {
    ...props,
    asterisk: asterisk ?? field.required,
    label: showLabel ? field.title : undefined,
    feedbackStatus:
      field.validateStatus === 'validating' ? 'pending' : field.validateStatus,
    feedbackText: hasErrors ? (
      <ErrorList errors={errors} vertical={feedbackLayout === 'popover'} />
    ) : (
      props.feedbackText
    )
  }
}

const InternalFormItem = observer((props: PropsWithChildren<FormItemProps>) => {
  const mergedProps = useFormItemProps(props)
  return <BaseFormItem {...mergedProps} />
})

export const FormItem = Object.assign(InternalFormItem, {
  Base: BaseFormItem
})
